/**
 * NitroPay ad-loader bootstrap.
 *
 * Loads NitroPay's per-site script (ads-XXXX.js) exactly once, and only when
 * the `ads` feature flag is on (staging now; localhost + prod stay off until
 * ADS_PROD_OVERRIDE flips — see src/config/featureFlags.js). When off, nothing is
 * injected and `window.nitroAds` never exists, so prod stays completely clean.
 *
 * The inline queue stub (window.nitroAds = {...queue}) is the standard NitroPay
 * pattern: it lets ad placements call createAd()/addUserToken() before the async
 * script finishes loading by buffering those calls into a queue the script
 * drains on load. We define it here (gated) rather than statically in
 * index.html so the gate covers it too.
 *
 * SPA route refresh is handled by `data-spa="auto"` on the script tag — NitroPay
 * auto-detects history changes and refreshes placements on navigation.
 *
 * Onboarding ref: https://help.nitropay.com/en/articles/12814107-onboarding-process
 */

import { FEATURE_FLAGS } from '../config/featureFlags';

/**
 * Per-site script identifier. Derived from the NitroPay panel URL in the
 * onboarding email (panel.nitropay.com/sites/2448). Confirm against the exact
 * <script src> shown in the panel's Setup tab if ads fail to load.
 */
export const NITROPAY_SITE_ID = '2448';

const SCRIPT_SRC = `https://s.nitropay.com/ads-${NITROPAY_SITE_ID}.js`;

let loaded = false;

/**
 * Inject the NitroPay loader. Idempotent and gated; safe to call on every app
 * mount. No-op when the `ads` flag is off or outside the browser.
 */
export function loadNitroPay() {
    if (loaded || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    if (!FEATURE_FLAGS.ads) {
        return;
    }
    loaded = true;

    // Queue stub — buffers createAd/addUserToken until the async script loads.
    window.nitroAds = window.nitroAds || {
        createAd: function () {
            return new Promise((resolve) => {
                window.nitroAds.queue.push(['createAd', arguments, resolve]);
            });
        },
        addUserToken: function () {
            window.nitroAds.queue.push(['addUserToken', arguments]);
        },
        queue: [],
    };

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.setAttribute('data-cfasync', 'false');
    script.setAttribute('data-spa', 'auto');
    document.head.appendChild(script);
}

/**
 * Create a single NitroPay ad placement, mounting into the DOM element whose id
 * is `id`. Gated by the `ads` flag; a no-op returning null when ads are off or
 * outside the browser. Safe to call before the async script finishes loading —
 * the queue stub buffers the call until it does. Returns the createAd promise.
 *
 * @param {string} id    id of the (already-rendered) container element.
 * @param {object} options NitroPay createAd options (format, sizes, etc.).
 */
export function createNitroAd(id, options = {}) {
    if (!FEATURE_FLAGS.ads || typeof window === 'undefined') {
        return Promise.resolve(null);
    }
    // Ensure the loader + queue stub exist before we create. On initial page
    // load a placement's mount effect can run BEFORE App's loader effect —
    // React runs child effects before parent effects — so window.nitroAds may
    // not exist yet (this is why the leaderboard ad, which mounts immediately at
    // `/`, never rendered while the deck ad, which mounts after async data load,
    // did). loadNitroPay() is idempotent and creates the stub synchronously, so
    // the createAd call below queues and runs once the async script loads.
    loadNitroPay();
    if (!window.nitroAds) {
        return Promise.resolve(null);
    }
    return window.nitroAds.createAd(id, options);
}
